import { useEffect, useState } from 'react';

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @mui material components
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

// Material Dashboard 2 React example components
import SidenavCollapse from 'examples/Sidenav/SidenavCollapse';
import { usePermissions } from 'hooks/usePermissions';

// Custom styles for the Sidenav
import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav';

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from 'context';

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const location = useLocation();
  const collapseName = location.pathname.replace('/', '');

  let textColor = 'white';

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = 'dark';
  } else if (whiteSidenav && darkMode) {
    textColor = 'inherit';
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that handles window resize and mobile state
    function handleResize() {
      const mobile = window.innerWidth < 1200;
      setIsMobile(mobile);
      
      if (mobile) {
        // On mobile, start with drawer closed and disable transparency/white mode
        setMiniSidenav(dispatch, true);
        setTransparentSidenav(dispatch, false);
        setWhiteSidenav(dispatch, false);
      } else {
        // On desktop, keep current transparency and white mode settings
        setTransparentSidenav(dispatch, transparentSidenav);
        setWhiteSidenav(dispatch, whiteSidenav);
      }
    }

    /** 
     The event listener that's calling the handleResize function when resizing the window.
    */
    window.addEventListener('resize', handleResize);

    // Call the handleResize function to set the state with the initial value.
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch, location]);

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  };

  const user = getUserData();
  const isSuperAdmin = user?.is_super_admin === true;
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  // Filter routes based on super admin status and, where set, a required permission
  const filteredRoutes = routes.filter(route => {
    if (route.superAdminOnly && !isSuperAdmin) {
      return false;
    }
    if (route.requiredPermission) {
      if (permissionsLoading) return false;
      if (!hasPermission(route.requiredPermission)) return false;
    }
    return true;
  });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const isRouteActive = (route, key) => {
    if (!route) return key === collapseName;
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const renderRoutes = filteredRoutes.filter(r => r.key !== 'sign-out').map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === 'collapse') {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: 'none' }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={isRouteActive(route, key)}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={isRouteActive(route, key)} />
        </NavLink>
      );
    } else if (type === 'title') {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === 'divider') {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  const dividerLight =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  return (
    <SidenavRoot
      {...rest}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? !miniSidenav : true}
      onClose={() => isMobile && setMiniSidenav(dispatch, true)}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <MDBox pt={3} pb={1} px={4} textAlign="center">
          <MDBox
            display={{ xs: 'block', xl: 'none' }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: 'pointer' }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox component={NavLink} to="/" display="flex" alignItems="center">
            {brand && <MDBox component="img" src={brand} alt="Brand" width="10rem" />}
            <MDBox
              width={!brandName && '100%'}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
                {brandName}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider light={dividerLight} />
        <List sx={{ flex: 1, overflowY: 'auto' }}>{renderRoutes}</List>
        <MDBox>
          <Divider light={dividerLight} />
          <NavLink to="/authentication/sign-out">
            <SidenavCollapse
              name="Sign Out"
              icon={<Icon fontSize="small">logout</Icon>}
              active={isRouteActive('/authentication/sign-out', 'sign-out')}
            />
          </NavLink>
        </MDBox>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: 'info',
  brand: '',
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
    'logo',
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
